import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';

import { firstValueFrom, take } from 'rxjs';

import { WidgetApiService } from 'src/app/api/queries/widget-api.service';
import { firstAvailableWidgetRow } from 'src/app/shared/utils/widget-list.utils';

import {
  createWidgetFail,
  createWidgetSuccess,
  updateWidgetSuccess,
} from '../actions/widget.actions';
import { Widget } from '../models/core.models';
import { selectSelectedPageId, selectSelectedPlaceholderId } from '../reducers';
import { CurrentViewWidgetsService } from './current-view-widgets.service';

/** The part of a create request a caller actually chooses. */
export interface CreateWidgetInput {
  objectId: string;
  objectClassId: number;
  chartId: number;
  chartTypeId: number;
  dataSourceObjectId: string;
  dataSourceObjectClassId: number;
}

/**
 * Widget writes whose payload is assembled from the **current view's widget list**.
 *
 * A service rather than query hooks because several callers are root services,
 * which by api-layer.md's consumer rule may not hold hooks. It injects
 * `WidgetApiService` directly, the sanctioned service-to-service call.
 */
@Injectable({ providedIn: 'root' })
export class WidgetPersistenceService {
  private readonly store = inject(Store);
  private readonly widgetApi = inject(WidgetApiService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly currentViewWidgets = inject(CurrentViewWidgetsService);

  /**
   * Merge `settingsPatch` into each widget's `settings` and PATCH the whole widget.
   *
   * `updateWidgetSuccess` is broadcast so `patchWidgetListCache$` writes the
   * server's version into the cached list - that is where the attached-dimension
   * chips come from - and `reloadWidgetAfterUpdate$` invalidates the widget's data.
   *
   * Failures are silent by design. Two rapid applies both complete, and the later
   * response wins the cache.
   */
  async applySettings(
    widgetIds: string[],
    settingsPatch: Record<string, unknown>,
  ): Promise<void> {
    await Promise.all(
      widgetIds.map(async (widgetId) => {
        const widget = this.currentViewWidgets.byId(widgetId);
        if (!widget) return;

        const payload = {
          ...widget,
          settings: { ...widget.settings, ...settingsPatch },
        };

        try {
          const updated = await firstValueFrom(
            this.widgetApi.updateWidget(widgetId, payload),
          );

          this.store.dispatch(updateWidgetSuccess({ widgetId, data: updated }));
        } catch {
          // * silent - see the note above
        }
      }),
    );
  }

  /**
   * Create a widget in the current view. Row and placeholder geometry come from
   * the list the view is rendering:
   * - `row` - the first row no real widget occupies
   * - `placeholderWidgetId` - the placeholder the user clicked, unless it is the
   *   server-side **fake** one (no DB row to reuse)
   * - `col` / `sizeX` / `sizeY` - that placeholder's geometry, so the new widget
   *   lands exactly where the tile was
   * - `page_id` - the selected dashboard page, null for a feed
   *
   * The **list is invalidated rather than patched**: the backend appends a freshly
   * positioned fake placeholder to every feed's `get_object_widgets` response, and
   * reproducing that by hand is not worth it.
   */
  async createWidget(input: CreateWidgetInput): Promise<void> {
    const placeholderId = await firstValueFrom(
      this.store.select(selectSelectedPlaceholderId).pipe(take(1)),
    );
    const pageId = await firstValueFrom(
      this.store.select(selectSelectedPageId).pipe(take(1)),
    );

    const widgets = this.currentViewWidgets.snapshot();
    const placeholder = placeholderId
      ? widgets.find((widget) => widget.id === placeholderId)
      : undefined;

    try {
      const widget = await firstValueFrom(
        this.widgetApi.createWidget({
          chartId: input.chartId,
          chartTypeId: input.chartTypeId,
          objectId: input.objectId,
          objectClassId: input.objectClassId,
          dataSourceObjectId: input.dataSourceObjectId,
          dataSourceObjectClassId: input.dataSourceObjectClassId,
          row: firstAvailableWidgetRow(widgets),
          placeholderWidgetId: placeholder?.is_fake ? null : placeholderId,
          col: placeholder?.col,
          sizeX: placeholder?.sizeX,
          sizeY: placeholder?.sizeY,
          pageId,
        }),
      );

      this.toastService.showSuccess(
        this.translateService.instant('widget.widget-created-successfully'),
      );

      this.currentViewWidgets.invalidate();
      this.store.dispatch(
        createWidgetSuccess({ widget, objectClassId: input.objectClassId }),
      );
    } catch (error) {
      // ! a failed create is silent by design - no toast
      this.store.dispatch(createWidgetFail({ error }));
    }
  }

  /**
   * Persist the whole view's layout. Called only by
   * `WidgetLayoutStagingService`, which owns the debounce, the search-string
   * guard and the pending-geometry overlay.
   */
  updateWidgetsOrder(widgets: Widget[]): Promise<unknown> {
    return firstValueFrom(this.widgetApi.updateWidgetsOrder(widgets));
  }
}
